var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import '../styles/collapsible.scss';
import ButtonComponent from './ButtonComponent';
import globalStore from '../store';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import FlipButton from './FlipButton';
import ToolTip from './ToolTip';
import { t } from 'i18next';
import { useOnResize } from '../helper/hooks/onResize';
import ToggleButton from './ToggleButton';
import { getContentLockArray } from '../helper/helper';
import { calculateTokenPosition } from '../helper/tokenDropHelper';
import { calcScale as calcScaleHelper } from '../helper/scaleHelper';
// Resolve the rect of the actual containing block for a positioned element.
// CSS resolves inline `left`/`top` against this block, not against the
// viewport — so when an ancestor like `.drawer-container` carries a
// `transform` (or other property that establishes a containing block), a
// `position: fixed` popup is positioned relative to that ancestor instead of
// the viewport, and any `position: absolute` popup is positioned relative to
// its nearest positioned ancestor. Returns `null` when the viewport itself
// is the containing block (caller should treat that as offset (0, 0)).
var findActualContainingBlockRect = function (el) {
    var cs = getComputedStyle(el);
    if (cs.position === 'fixed') {
        var node = el.parentElement;
        while (node && node !== document.body && node !== document.documentElement) {
            var ncs = getComputedStyle(node);
            var formsCB = ncs.transform !== 'none' ||
                ncs.perspective !== 'none' ||
                (ncs.filter && ncs.filter !== 'none') ||
                ncs.willChange === 'transform' ||
                ncs.willChange === 'perspective' ||
                ncs.willChange === 'filter' ||
                ncs.contain === 'paint' ||
                ncs.contain === 'layout' ||
                ncs.contain === 'strict' ||
                ncs.contain === 'content';
            if (formsCB)
                return node.getBoundingClientRect();
            node = node.parentElement;
        }
        return null;
    }
    if (cs.position === 'absolute') {
        var op = el.offsetParent;
        return op ? op.getBoundingClientRect() : null;
    }
    return null;
};
var CollapsibleDiv = function (props) {
    var mainElement = props.mainElement, elements = props.elements, onSelect = props.onSelect, mainEleDefaultCSS = props.mainEleDefaultCSS, _a = props.mainEleNormalCSS, mainEleNormalCSS = _a === void 0 ? 'mainEleNormalCSS' : _a, elemsDefaultCSS = props.elemsDefaultCSS, _b = props.elemsNormalCSS, elemsNormalCSS = _b === void 0 ? 'elemsNormalCSS' : _b, containerCSS = props.containerCSS, collapsibleContainerCSS = props.collapsibleContainerCSS, _c = props.openAt, openAt = _c === void 0 ? 'right' : _c, direction = props.direction, _d = props.isParentRelative, isParentRelative = _d === void 0 ? false : _d, _e = props.isTooltipRelative, isTooltipRelative = _e === void 0 ? false : _e, isInnerTooltipRelative = props.isInnerTooltipRelative, _f = props.showToolTip, showToolTip = _f === void 0 ? false : _f, title = props.title, hr = props.hr, _g = props.zoomLevel, zoomLevel = _g === void 0 ? 1 : _g, _h = props.isWhiteBoard, isWhiteBoard = _h === void 0 ? false : _h, _j = props.type, type = _j === void 0 ? 'textType1' : _j, innerBtnType = props.innerBtnType, _k = props.disabled, disabled = _k === void 0 ? false : _k, _l = props.disabledElements, disabledElements = _l === void 0 ? [] : _l, _m = props.hiddenElements, hiddenElements = _m === void 0 ? [] : _m, _o = props.activeElements, activeElements = _o === void 0 ? [] : _o, _p = props.activeElementIds, activeElementIds = _p === void 0 ? [] : _p, _q = props.parentType, parentType = _q === void 0 ? '' : _q, _r = props.toggleTextVisible, toggleTextVisible = _r === void 0 ? true : _r, toggleBtnClassName = props.toggleBtnClassName, toogleTooltipDirection = props.toogleTooltipDirection, _s = props.addButtonToToggle, addButtonToToggle = _s === void 0 ? false : _s, ariaLabel = props.ariaLabel, ariaLabelTranslated = props.ariaLabelTranslated, _t = props.controlledCollapse, controlledCollapse = _t === void 0 ? false : _t, onCollapseClick = props.onCollapseClick, collapsibleOpen = props.collapsibleOpen, _u = props.useViewportPosition, useViewportPosition = _u === void 0 ? false : _u, _v = props.mainActiveOnSelection, mainActiveOnSelection = _v === void 0 ? false : _v;
    var _w = globalStore.useStoreState(function (state) { return state.playerStore; }), displayMode = _w.displayMode, isAdmin = _w.isAdmin, playerIconZoom = _w.playerIconZoom;
    var _x = useContext(DataContext), selectedTool = _x.selectedTool, setSelectedTool = _x.setSelectedTool, manipulative = _x.manipulative, text = _x.text, table = _x.table, annotations = _x.annotations, shades = _x.shades, setText = _x.setText, setTable = _x.setTable, setAnnotations = _x.setAnnotations, setManipulative = _x.setManipulative, setShades = _x.setShades;
    var _y = useState('collapse'), collapseState = _y[0], setCollapseState = _y[1];
    var _z = useState('collapsed'), collapseClass = _z[0], setCollapseClass = _z[1];
    var _0 = useState(collapsibleOpen !== undefined ? collapsibleOpen : false), open = _0[0], setOpen = _0[1];
    var _1 = useState(0), offsetY = _1[0], setOffsetY = _1[1];
    var _2 = useState(0), offsetX = _2[0], setOffsetX = _2[1];
    // Stays false until the useLayoutEffect below measures the just-mounted
    // panel. The panel is hidden until then so the one-frame flash at the
    // wrong origin (offsets=0, or stale offsets from a previous open) is gone.
    var _3 = useState(false), hasMeasuredOffsets = _3[0], setHasMeasuredOffsets = _3[1];
    var _4 = useState(false), isAnimating = _4[0], setIsAnimating = _4[1];
    var _5 = useState({ x: 0, y: 0, width: 40, height: 40 }), btnPos = _5[0], setBtnPos = _5[1];
    var _6 = useState(false), showLock = _6[0], setShowLock = _6[1];
    var _7 = useState(false), lockAbove = _7[0], setLockAbove = _7[1];
    var lockBtnRef = useRef(null);
    // Stores the content-lock state of the currently selected table cell.
    // Updated synchronously via 'tableCellLockState' event so the lock icon
    // reflects the correct state in the very first render after cell selection.
    var _8 = useState(false), cellContentLocked = _8[0], setCellContentLocked = _8[1];
    var _9 = useState(false), selectModeActive = _9[0], setSelectModeActive = _9[1];
    var _10 = useState(false), mouseOver = _10[0], setMouseOver = _10[1];
    var panelId = useId();
    var _11 = useState(mainElement), mainBtnImages = _11[0], setMainBtnImages = _11[1];
    var _12 = useState(elements), optionBtnImages = _12[0], setOptionBtnImages = _12[1];
    var paddingAroundBtn = 5;
    var gap = 10;
    var panelRef = useRef(null);
    var btnRef = useRef(null);
    var optionRefs = useRef([]);
    var containerRef = useRef(null);
    // Track whether collapse/expand was triggered by user click (vs programmatic)
    var userInitiatedRef = useRef(false);
    useEffect(function () {
        if (collapsibleOpen !== undefined) {
            setOpen(collapsibleOpen);
            if (collapsibleOpen) {
                setCollapseState('expand');
            }
            else {
                setCollapseState('collapse');
            }
        }
    }, [collapsibleOpen]);
    useEffect(function () {
        return function () {
            if (open === false) {
                setShowLock(false);
            }
        };
    }, [open]);
    // Sync lock state when selection or table data changes.
    // When no table cell is selected: reset showLock and cellContentLocked.
    // When a table cell IS selected: sync cellContentLocked from table data so it's
    // always up-to-date — covers lock toggles, re-selection after click-outside, and
    // any table state change that affects the editing lock.
    useEffect(function () {
        var hasExpandableLock = selectedTool.some(function (t) {
            return t.startsWith('tableText_') ||
                t.startsWith('numberFrame_') ||
                t.startsWith('text_') ||
                t.startsWith('mathtext_') ||
                t.startsWith('annotations_');
        });
        // Also keep showLock alive for numberLine/jumpBar/coloredTiles (they have their own lock popup)
        var hasNLLock = selectedTool.some(function (t) { return t.startsWith('numberLine_') || t.startsWith('jumpBar_') || t.startsWith('coloredTiles_'); });
        if (!hasExpandableLock && !hasNLLock) {
            setShowLock(false);
            setCellContentLocked(false);
        }
        else if (!selectModeActive) {
            var lockObj = contentLockState();
            setCellContentLocked(lockObj.editing);
        }
    }, [selectedTool, table, text, annotations, manipulative, selectModeActive]);
    // Listen for cell content-lock state changes dispatched by TableManipulativeComp.
    // The event fires synchronously inside the React mousedown handler so the state
    // update is batched with setSelectedTool — both resolve in one render, no lag.
    useEffect(function () {
        var handler = function (d) { return setCellContentLocked(d.locked); };
        customEventHandler.addEventListener('tableCellLockState', handler);
        return function () { return customEventHandler.removeEventListener('tableCellLockState', handler); };
    }, []);
    useEffect(function () {
        var handler = function (d) { return setSelectModeActive(d.active); };
        customEventHandler.addEventListener('selectModeChanged', handler);
        return function () { return customEventHandler.removeEventListener('selectModeChanged', handler); };
    }, []);
    useEffect(function () {
        setMainBtnImages(mainElement);
    }, [mainElement]);
    useEffect(function () {
        setOptionBtnImages(elements);
    }, [elements]);
    // Filter out hidden elements
    var visibleElements = elements.filter(function (elem) {
        if (!elem.id)
            return true;
        return !hiddenElements.includes(elem.id);
    });
    useEffect(function () {
        var timeOut;
        var wasUserInitiated = userInitiatedRef.current;
        userInitiatedRef.current = false;
        if (collapseState === 'collapse') {
            // setIsAnimating(true);
            setCollapseClass('collapsing');
            timeOut = setTimeout(function () {
                var _a;
                setCollapseClass('collapsed');
                // setIsAnimating(false);
                if (wasUserInitiated)
                    (_a = btnRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 300);
        }
        else {
            // setIsAnimating(true);
            setCollapseClass('expanding');
            timeOut = setTimeout(function () {
                // setIsAnimating(false);
                setCollapseClass('expanded');
                if (wasUserInitiated) {
                    setTimeout(function () {
                        var _a;
                        (_a = optionRefs.current[0]) === null || _a === void 0 ? void 0 : _a.focus();
                    }, 10);
                }
            }, 300);
        }
        return function () {
            clearTimeout(timeOut);
        };
    }, [collapseState]);
    var updateBtnPosition = function () {
        if (!btnRef.current || !containerRef.current)
            return;
        // const container = document.documentElement;
        // if (!container) return;
        var parentRect = containerRef.current.parentElement.getBoundingClientRect();
        var btnRect = btnRef.current.getBoundingClientRect();
        // const scrollLeft = container.scrollLeft;
        // const scrollTop = container.scrollTop;
        var scrollLeft = 0;
        var scrollTop = 0;
        // For non-parent-relative mode the popup expects coordinates in its
        // *containing block*, not raw viewport. CSS resolves inline `left`/`top`
        // against the containing block, so when an ancestor like `.drawer-container`
        // carries a `transform` (which establishes the CB for any `position: fixed`
        // descendant) viewport coords would land the popup hundreds of pixels off.
        // The parent-relative branch already does this — `parentRect.left` is the
        // CB for the typical `position: absolute` setup where the inline wrapper
        // has `position: relative`. Mirror that behaviour for the viewport branch
        // by subtracting the popup's actual containing block.
        var cbLeft = 0;
        var cbTop = 0;
        if (!isParentRelative && panelRef.current) {
            var cbRect = findActualContainingBlockRect(panelRef.current);
            if (cbRect) {
                cbLeft = cbRect.left;
                cbTop = cbRect.top;
            }
        }
        var _btnPos = {
            x: isParentRelative
                ? (btnRect.left - parentRect.left + scrollLeft) / zoomLevel
                : (btnRect.left - cbLeft + scrollLeft) / zoomLevel,
            y: isParentRelative
                ? (btnRect.top - parentRect.top + scrollTop) / zoomLevel
                : (btnRect.top - cbTop + scrollTop) / zoomLevel,
            width: btnRect.width / zoomLevel,
            height: btnRect.height / zoomLevel,
        };
        setBtnPos(_btnPos);
    };
    var toggleCollapsible = function () {
        // if (isAnimating) return;
        userInitiatedRef.current = true;
        updateBtnPosition();
        onCollapseClick && onCollapseClick(!open);
        setOpen(function (prev) { return !prev; });
        if (collapseState === 'collapse') {
            setCollapseState('expand');
        }
        else {
            setCollapseState('collapse');
        }
        // onSelect()
    };
    var handleResize = useCallback(function () {
        if (!open)
            return;
        updateBtnPosition();
        if (panelRef.current) {
            setOffsetY(-panelRef.current.getBoundingClientRect().height);
            setOffsetX(-panelRef.current.getBoundingClientRect().width);
        }
    }, [open, zoomLevel, isParentRelative, displayMode, playerIconZoom]);
    useOnResize(handleResize);
    var selectItem = function (item, subItem) {
        userInitiatedRef.current = true;
        onSelect(item, subItem);
        if (!controlledCollapse) {
            if (subItem === undefined)
                setCollapseState('collapse');
            setOpen(function (prev) { return !prev; });
        }
    };
    var calcScale = function () { return calcScaleHelper(selectedTool, manipulative, table); };
    /** Disable scale buttons when spinner is mixed with other manipulative types,
     *  or when locked pattern shapes are selected */
    var isScaleDisabled = function () {
        if (selectedTool.length >= 2) {
            var hasSpinner = selectedTool.some(function (s) { return s.startsWith('spinner'); });
            var hasOther = selectedTool.some(function (s) { return !s.startsWith('spinner'); });
            if (hasSpinner && hasOther)
                return true;
        }
        // Disable scale when jumpBar is selected without its parent numberLine
        var hasJumpBar = selectedTool.some(function (s) { return s.startsWith('jumpBar_'); });
        if (hasJumpBar) {
            var hasNumberLine = selectedTool.some(function (s) { return s.startsWith('numberLine_'); });
            if (!hasNumberLine)
                return true;
        }
        // Disable scale when only customTick(s) are selected
        if (selectedTool.every(function (s) { return s.startsWith('customTick_'); }))
            return true;
        // Disable scale for locked pattern shapes
        if (selectedTool.length > 0) {
            var allLockedPS = selectedTool.every(function (toolId) {
                var _a, _b, _c;
                if (toolId.startsWith('patternShapes_')) {
                    var item = manipulative.find(function (m) { return m.data.id === toolId; });
                    return ((_a = item === null || item === void 0 ? void 0 : item.data) === null || _a === void 0 ? void 0 : _a.type) === 'patternShapes' && ((_c = (_b = item === null || item === void 0 ? void 0 : item.data) === null || _b === void 0 ? void 0 : _b.lock) === null || _c === void 0 ? void 0 : _c.positioning);
                }
                if (toolId.startsWith('manipulativeGroup_')) {
                    var members = manipulative.filter(function (m) { return m.data.groupId === toolId; });
                    return (members.length > 0 &&
                        members.every(function (m) {
                            var _a;
                            return (m.data.type === 'patternShapes' ||
                                m.data.type === 'numberPiece' ||
                                m.data.type === 'moneyPiece') &&
                                ((_a = m.data.lock) === null || _a === void 0 ? void 0 : _a.positioning);
                        }));
                }
                return false;
            });
            if (allLockedPS)
                return true;
        }
        return false;
    };
    var contentLockState = function () {
        return getContentLockArray({ selectedTool: selectedTool, text: text, table: table, annotations: annotations, manipulative: manipulative, shades: shades });
    };
    var jumpBarLengthLockState = function () {
        var _a, _b;
        var _loop_1 = function (toolId) {
            if (toolId.startsWith('jumpBar_')) {
                var item = manipulative.find(function (m) { return m.data.id === toolId; });
                if ((_b = (_a = item === null || item === void 0 ? void 0 : item.data) === null || _a === void 0 ? void 0 : _a.lock) === null || _b === void 0 ? void 0 : _b.length)
                    return { value: true };
            }
            if (toolId.startsWith('numberLine_')) {
                var childBars = manipulative.filter(function (m) { return m.data.numberLineId === toolId; });
                if (childBars.some(function (m) { var _a; return (_a = m.data.lock) === null || _a === void 0 ? void 0 : _a.length; }))
                    return { value: true };
            }
        };
        for (var _i = 0, selectedTool_1 = selectedTool; _i < selectedTool_1.length; _i++) {
            var toolId = selectedTool_1[_i];
            var state_1 = _loop_1(toolId);
            if (typeof state_1 === "object")
                return state_1.value;
        }
        return false;
    };
    var toggleLockState = function (lockIndex) {
        var lockObj = contentLockState();
        // If lockIndex provided, toggle individual lock; otherwise toggle both
        var key = lockIndex === 0
            ? 'editing'
            : lockIndex === 1
                ? 'positioning'
                : lockIndex === 2
                    ? 'jumpBarLength'
                    : null;
        var lockKey = key === 'editing' || key === 'positioning' ? key : null;
        var newState = key === 'jumpBarLength'
            ? !jumpBarLengthLockState()
            : lockKey
                ? !lockObj[lockKey]
                : !lockObj.editing;
        selectedTool.forEach(function (toolId) {
            if (toolId.startsWith('text_')) {
                var textIndex_1 = Number(toolId.split('_')[1]);
                setText(function (prev) {
                    var updated = __spreadArray([], prev, true);
                    var item = updated[textIndex_1];
                    if (!item.data.lock) {
                        item.data.lock = { positioning: false, editing: false };
                    }
                    if (key === 'editing' || key === 'positioning') {
                        item.data.lock[key] = newState;
                    }
                    else if (!key) {
                        item.data.lock['editing'] = newState;
                        item.data.lock['positioning'] = newState;
                    }
                    return updated;
                });
            }
            if (toolId.startsWith('annotations_')) {
                var index_1 = Number(toolId.split('_')[1]);
                setAnnotations(function (prev) {
                    var updated = __spreadArray([], prev, true);
                    var item = updated[index_1];
                    if (!item.data.lock) {
                        item.data.lock = { positioning: false, editing: false };
                    }
                    if (key === 'editing' || key === 'positioning') {
                        item.data.lock[key] = newState;
                    }
                    else if (!key) {
                        item.data.lock['editing'] = newState;
                        item.data.lock['positioning'] = newState;
                    }
                    return updated;
                });
            }
            if (toolId.startsWith('tableText_')) {
                var _a = toolId.split('_'), tableIndexStr = _a[1];
                var tableIndex_1 = Number(tableIndexStr);
                if (!key || key === 'positioning') {
                    // Position lock is table-level
                    setTable(function (prev) {
                        var updatedTable = __spreadArray([], prev, true);
                        var tableItem = __assign({}, updatedTable[tableIndex_1]);
                        var tableData = __assign({}, tableItem.data);
                        if (!tableData.lock) {
                            tableData.lock = { positioning: false, editing: false };
                        }
                        tableData.lock.positioning = key ? newState : newState;
                        tableItem.data = tableData;
                        updatedTable[tableIndex_1] = tableItem;
                        return updatedTable;
                    });
                }
                if (!key || key === 'editing') {
                    // Content lock is per-cell: dispatch event so TableManipulativeComp applies it
                    customEventHandler.dispatchEvent('tableContentLockToggle', {
                        tableIndex: tableIndex_1,
                        newState: newState,
                    });
                }
            }
            if (toolId.startsWith('shades_')) {
                var splitId = toolId.split('_');
                var shadeIndex_1 = Number(splitId[splitId.length - 1]);
                setShades(function (prev) {
                    var updated = __spreadArray([], prev, true);
                    if (updated[shadeIndex_1]) {
                        var updatedLock = __assign({}, updated[shadeIndex_1].data.lock);
                        if (lockKey) {
                            updatedLock[lockKey] = newState;
                        }
                        else {
                            updatedLock.editing = newState;
                            updatedLock.positioning = newState;
                        }
                        updated[shadeIndex_1] = __assign(__assign({}, updated[shadeIndex_1]), { data: __assign(__assign({}, updated[shadeIndex_1].data), { lock: updatedLock }) });
                    }
                    return updated;
                });
            }
            if (toolId.startsWith('linkingCube') ||
                toolId.startsWith('numberChart') ||
                toolId.startsWith('spinner') ||
                toolId.startsWith('analog_clock') ||
                toolId.startsWith('timer') ||
                toolId.startsWith('dice_') ||
                toolId.startsWith('deck_') ||
                toolId.startsWith('numberRacks') ||
                toolId.startsWith('arrayModel') ||
                toolId.startsWith('protractor') ||
                toolId.startsWith('fractionTiles_') ||
                toolId.startsWith('patternShapes_') ||
                toolId.startsWith('moneyPiece_') ||
                toolId.startsWith('numberPiece_') ||
                toolId.startsWith('coloredTiles') ||
                toolId.startsWith('linearPiece_') ||
                toolId.startsWith('counter_') ||
                toolId.startsWith('numberFrameCounter_') ||
                toolId.startsWith('coinsAndCounters_')) {
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        var _a;
                        var isFractionTileMatch = item.data.type === 'fractionTiles' && toolId.startsWith((_a = item.data.id) !== null && _a !== void 0 ? _a : '');
                        if (item.data.id === toolId || isFractionTileMatch) {
                            var updatedLock = __assign({}, item.data.lock);
                            if (lockKey) {
                                updatedLock[lockKey] = newState;
                            }
                            else {
                                updatedLock.editing = newState;
                                updatedLock.positioning = newState;
                            }
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lock: updatedLock }) });
                        }
                        else {
                            return item;
                        }
                    });
                });
                // When locking label editing for a fraction tile, normalize selectedTool
                // to the bare tile ID so the label loses its focused/selected visual state.
                if (toolId.startsWith('fractionTiles_') &&
                    (lockKey === 'editing' || !lockKey) &&
                    newState === true) {
                    var tileItem = manipulative.find(function (m) { var _a; return m.data.type === 'fractionTiles' && toolId.startsWith((_a = m.data.id) !== null && _a !== void 0 ? _a : ''); });
                    if ((tileItem === null || tileItem === void 0 ? void 0 : tileItem.data.id) && !selectedTool.includes(tileItem.data.id)) {
                        setSelectedTool([tileItem.data.id]);
                    }
                }
            }
            if (toolId.startsWith('card_')) {
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        var _a;
                        if (item.data.type !== 'deck')
                            return item;
                        var hasCard = (_a = item.data.cards) === null || _a === void 0 ? void 0 : _a.some(function (card) { return card.id === toolId; });
                        if (!hasCard)
                            return item;
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { cards: item.data.cards.map(function (card) {
                                    var _a;
                                    return card.id === toolId
                                        ? __assign(__assign({}, card), { lock: __assign(__assign({}, card.lock), (_a = {}, _a['editing'] = newState, _a['positioning'] = newState, _a)) }) : card;
                                }) }) });
                    });
                });
            }
            if (toolId.startsWith('numberLine_') || toolId.startsWith('jumpBar_')) {
                if (key === 'jumpBarLength') {
                    if (toolId.startsWith('jumpBar_')) {
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                if (item.data.id !== toolId)
                                    return item;
                                var currentLock = item.data.lock || {};
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lock: __assign(__assign({}, currentLock), { length: newState }) }) });
                            });
                        });
                    }
                    else {
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                if (item.data.numberLineId !== toolId)
                                    return item;
                                var currentLock = item.data.lock || {};
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lock: __assign(__assign({}, currentLock), { length: newState }) }) });
                            });
                        });
                    }
                }
                else {
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.id !== toolId)
                                return item;
                            var currentLock = item.data.lock || {};
                            var newLock;
                            if (key === 'editing') {
                                newLock = __assign(__assign({}, currentLock), { editing: newState });
                            }
                            else if (key === 'positioning') {
                                newLock = __assign(__assign({}, currentLock), { positioning: newState });
                            }
                            else {
                                newLock = __assign(__assign({}, currentLock), { positioning: newState, editing: newState, length: newState });
                            }
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lock: newLock }) });
                        });
                    });
                }
            }
            if (toolId.startsWith('numberFrame_')) {
                setManipulative(function (prev) {
                    var frameItem = prev.find(function (m) { return m.data.id === toolId; });
                    var frameData = frameItem === null || frameItem === void 0 ? void 0 : frameItem.data;
                    var isPositioningLock = key === 'positioning' || key === null;
                    return prev.map(function (item) {
                        var _a;
                        var isFrame = item.data.id === toolId;
                        var isAttachedCounter = item.data.type === 'numberFrameCounter' && item.data.frameId === toolId;
                        if (isFrame || isAttachedCounter) {
                            var center = item.data.center;
                            // Snap counter center to its calculated frame position before locking
                            // so actualPosition (which falls back to center when locked) doesn't shift
                            if (isAttachedCounter && isPositioningLock && newState && (frameData === null || frameData === void 0 ? void 0 : frameData.tokens)) {
                                var cellIndex = -1;
                                for (var _i = 0, _b = Object.entries(frameData.tokens); _i < _b.length; _i++) {
                                    var _c = _b[_i], ci = _c[0], tokenId = _c[1];
                                    if (tokenId === item.data.id) {
                                        cellIndex = parseInt(ci);
                                        break;
                                    }
                                }
                                if (cellIndex !== -1) {
                                    center = calculateTokenPosition(frameData, cellIndex);
                                }
                            }
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: center, lock: key
                                        ? __assign(__assign({}, item.data.lock), (_a = {}, _a[key] = newState, _a)) : { positioning: newState, editing: newState } }) });
                        }
                        return item;
                    });
                });
            }
            if (toolId.startsWith('manipulativeGroup_')) {
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        var _a;
                        if (item.data.groupId === toolId) {
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lock: __assign(__assign({}, item.data.lock), (_a = {}, _a['editing'] = newState, _a['positioning'] = newState, _a)) }) });
                        }
                        else {
                            return item;
                        }
                    });
                });
            }
        });
    };
    useLayoutEffect(function () {
        var measure = function () {
            if (panelRef.current) {
                var h = panelRef.current.getBoundingClientRect().height;
                var w = panelRef.current.getBoundingClientRect().width;
                setOffsetY(-h); // move upward exactly its full rendered height
                setOffsetX(-w);
                setHasMeasuredOffsets(true);
            }
            updateBtnPosition();
        };
        measure();
        // Re-measure on the next frame to catch ancestor layouts that settle
        // asynchronously after a CSS-var-driven size change. Concretely: when
        // `--playerIcon-Zoom` flips, the sidebar's flex children resize and the
        // sidebar's `width: fit-content` recomputes. The first synchronous
        // measure here can land before that ancestor reflow propagates to
        // `btnRef.getBoundingClientRect()`, leaving the popup anchored to the
        // pre-zoom button position. The rAF pass picks up the settled layout.
        var id = requestAnimationFrame(measure);
        return function () { return cancelAnimationFrame(id); };
        // `collapseClass` is in the deps so measurement fires after the panel is
        // actually mounted (collapseClass transitions 'collapsed' → 'expanding').
        // Without it, the effect runs when `open` flips but panelRef is still
        // null because the panel hasn't rendered yet, and the next render that
        // mounts the panel never re-triggers measurement.
    }, [optionBtnImages, openAt, displayMode, playerIconZoom, zoomLevel, open, collapseClass]);
    // Reset measurement flag when the panel fully collapses so the next open
    // starts hidden until the new size is measured.
    useEffect(function () {
        if (collapseClass === 'collapsed')
            setHasMeasuredOffsets(false);
    }, [collapseClass]);
    // Close the collapsible when the nearest scroll container scrolls
    // (e.g. sidebar CustomScrollbar on iPad when icons overflow)
    useEffect(function () {
        if (!open || !btnRef.current)
            return;
        var scrollContainer = btnRef.current.closest('.customScrollbarContent, .sidebarTop--touch');
        if (!scrollContainer)
            return;
        // Capture the scroll position at the moment the popup opens.
        // Portaling + React re-renders can trigger spurious scroll events;
        // only react when scrollTop/Left actually changes.
        var startScrollTop = scrollContainer.scrollTop;
        var startScrollLeft = scrollContainer.scrollLeft;
        var onScroll = function () {
            if (scrollContainer.scrollTop === startScrollTop &&
                scrollContainer.scrollLeft === startScrollLeft) {
                return;
            }
            userInitiatedRef.current = true;
            setOpen(false);
            setCollapseState('collapse');
            onCollapseClick && onCollapseClick(false);
        };
        scrollContainer.addEventListener('scroll', onScroll, { passive: true });
        return function () { return scrollContainer.removeEventListener('scroll', onScroll); };
    }, [open, onCollapseClick]);
    var isPanelOpen = collapseClass === 'expanded' || collapseClass === 'expanding';
    var calcOptionHolderCss = useCallback(function () {
        var _a;
        var _btnPos = __assign({}, btnPos);
        var _left;
        var _top;
        var _paddingLeft;
        var _paddingBottom;
        var _paddingRight;
        var _paddingTop;
        switch (openAt) {
            case 'right':
                _left = _btnPos.x - paddingAroundBtn;
                _top = _btnPos.y - paddingAroundBtn;
                if (mainEleNormalCSS === 'colorSBtn') {
                    _paddingLeft = _btnPos.width + gap + 10;
                }
                else {
                    _paddingLeft = _btnPos.width + gap;
                }
                _paddingBottom = undefined;
                _paddingRight = undefined;
                _paddingTop = undefined;
                break;
            case 'top':
                _left = _btnPos.x - paddingAroundBtn;
                _top = _btnPos.y + offsetY + _btnPos.height + paddingAroundBtn;
                _paddingBottom = _btnPos.height + gap;
                _paddingLeft = undefined;
                _paddingRight = undefined;
                _paddingTop = undefined;
                break;
            case 'left':
                _left = _btnPos.x + offsetX + paddingAroundBtn + _btnPos.width;
                _top = _btnPos.y - paddingAroundBtn;
                _paddingRight = _btnPos.width + gap;
                _paddingBottom = undefined;
                _paddingLeft = undefined;
                _paddingTop = undefined;
                break;
            case 'bottom':
                _left = _btnPos.x - paddingAroundBtn;
                _top = _btnPos.y - paddingAroundBtn;
                _paddingTop = _btnPos.height + gap;
                _paddingLeft = undefined;
                _paddingBottom = undefined;
                _paddingRight = undefined;
                break;
        }
        return _a = {
                left: _left,
                top: _top,
                paddingLeft: _paddingLeft,
                paddingBottom: _paddingBottom,
                paddingRight: _paddingRight,
                paddingTop: _paddingTop
            },
            // background: 'red',
            _a['--paddingAroundBtn'] = paddingAroundBtn + 'px',
            _a['--btnGap'] = gap + 'px',
            _a;
    }, [
        btnPos.x,
        btnPos.y,
        btnPos.width,
        btnPos.height,
        open,
        openAt,
        displayMode,
        offsetY,
        offsetX,
    ]);
    useEffect(function () {
        function handleClickOutside(event) {
            var container = containerRef.current;
            var panel = panelRef.current;
            var target = event.target;
            // Treat clicks inside the container OR inside the portaled popup panel as "inside"
            var insideContainer = container && container.contains(target);
            var insidePanel = panel && panel.contains(target);
            if (!insideContainer && !insidePanel) {
                setCollapseState('collapse');
                setOpen(false);
            }
        }
        if (open && !controlledCollapse) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }
        return function () {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [open, controlledCollapse]);
    // Close submenu when focus moves outside the container (e.g., parent roving tabindex moves to another item)
    useEffect(function () {
        if (!open || controlledCollapse)
            return;
        var container = containerRef.current;
        if (!container)
            return;
        var handleFocusOut = function (e) {
            var relatedTarget = e.relatedTarget;
            var panel = panelRef.current;
            // Only close if focus is moving to a known element outside BOTH the container AND the portaled popup panel
            if (relatedTarget &&
                !container.contains(relatedTarget) &&
                !(panel && panel.contains(relatedTarget))) {
                setCollapseState('collapse');
                setOpen(false);
                onCollapseClick && onCollapseClick(false);
            }
        };
        container.addEventListener('focusout', handleFocusOut);
        // Also listen on the portaled panel so focus movements within it don't close the popup
        var panel = panelRef.current;
        panel === null || panel === void 0 ? void 0 : panel.addEventListener('focusout', handleFocusOut);
        return function () {
            container.removeEventListener('focusout', handleFocusOut);
            panel === null || panel === void 0 ? void 0 : panel.removeEventListener('focusout', handleFocusOut);
        };
    }, [open, controlledCollapse, onCollapseClick]);
    useEffect(function () {
        if (disabled) {
            setOpen(false);
            setCollapseState('collapse');
        }
    }, [disabled]);
    return (_jsxs("div", { style: {
            zIndex: useViewportPosition ? undefined : open || mouseOver ? 10 : undefined,
        }, ref: containerRef, className: "collapsibleRight ".concat(collapsibleContainerCSS), children: [_jsx(ButtonComponent, { title: title, ariaLabel: ariaLabel, ariaLabelTranslated: ariaLabelTranslated, imgSrc: mainBtnImages, defaultCSS: "collapsibleMainBtn ".concat(mainEleDefaultCSS), normalCSS: mainEleNormalCSS, clickHandler: toggleCollapsible, reference: btnRef, disabled: disabled || isAnimating, direction: direction, isParentRelative: isTooltipRelative, showToolTip: showToolTip && !(useViewportPosition && open), useViewportPosition: useViewportPosition, clickActive: true, controlActiveFromOutside: mainActiveOnSelection, isActive: open || (mainActiveOnSelection && typeof activeElements === 'number'), isWhiteBoard: isWhiteBoard, onMouseOver: function () { return setMouseOver(true); }, onMouseOut: function () { return setMouseOver(false); }, type: type, ariaExpanded: open, ariaControls: panelId, ariaHaspopup: true }), useViewportPosition && open && showToolTip && mouseOver && (_jsx(ToolTip, { buttonHovered: mouseOver, text: title ? t(title) : '', direction: 'right', isWhiteBoard: isWhiteBoard, useViewportPosition: true })), collapseClass !== 'collapsed' && (_jsx("div", { ref: panelRef, id: panelId, role: "toolbar", "aria-label": ariaLabel ? t(ariaLabel) : undefined, "aria-orientation": "horizontal", className: "collapsibleOptionHolder ".concat("open_".concat(openAt), "  ").concat(collapseClass, "_").concat(openAt, " ").concat(containerCSS), style: __assign(__assign({}, calcOptionHolderCss()), { 
                    // Hide until offsets are measured — prevents a one-frame flash
                    // where the panel briefly lands at the wrong origin (offsets=0).
                    visibility: hasMeasuredOffsets ? undefined : 'hidden' }), inert: collapseClass !== 'expanded' && collapseClass !== 'expanding' ? true : undefined, "aria-hidden": collapseClass !== 'expanded' && collapseClass !== 'expanding', onKeyDown: function (e) {
                    var _a;
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        e.stopPropagation();
                        userInitiatedRef.current = true;
                        setOpen(false);
                        setCollapseState('collapse');
                        onCollapseClick && onCollapseClick(false);
                        return;
                    }
                    // Arrow key navigation within submenu (all 4 arrows)
                    var prevKeys = ['ArrowLeft', 'ArrowUp'];
                    var nextKeys = ['ArrowRight', 'ArrowDown'];
                    if (prevKeys.includes(e.key) ||
                        nextKeys.includes(e.key) ||
                        e.key === 'Home' ||
                        e.key === 'End') {
                        e.preventDefault();
                        e.stopPropagation();
                        var panel = panelRef.current;
                        if (!panel)
                            return;
                        var focusableItems = Array.from(panel.querySelectorAll('button:not([disabled]):not([aria-hidden="true"])')).filter(function (el) { return el.offsetParent !== null; });
                        if (focusableItems.length === 0)
                            return;
                        var currentIndex = focusableItems.indexOf(document.activeElement);
                        var nextIndex = void 0;
                        if (e.key === 'Home') {
                            nextIndex = 0;
                        }
                        else if (e.key === 'End') {
                            nextIndex = focusableItems.length - 1;
                        }
                        else if (nextKeys.includes(e.key)) {
                            nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % focusableItems.length;
                        }
                        else {
                            nextIndex =
                                currentIndex === -1
                                    ? 0
                                    : (currentIndex - 1 + focusableItems.length) % focusableItems.length;
                        }
                        (_a = focusableItems[nextIndex]) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                }, children: !disabled &&
                    visibleElements.map(function (_elem, _ind) {
                        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                        var isElementDisabled = _elem.id ? disabledElements.includes(_elem.id) : false;
                        // Find the original index in the elements array
                        var originalIndex = elements.findIndex(function (e) { return e === _elem; });
                        if (_elem.type === 'toggleBtn') {
                            return (_jsx(ToggleButton, { options: _elem.elements, textVisible: toggleTextVisible, initial: (_a = _elem.selectedValue) !== null && _a !== void 0 ? _a : (_c = (_b = _elem.elements) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.value, className: toggleBtnClassName, toogleTooltipDirection: toogleTooltipDirection, isParentRelative: true, onChange: function (item) { return selectItem(originalIndex, item); }, addButtonToToggle: addButtonToToggle, tabIndex: isPanelOpen ? 0 : -1, ariaLabel: _elem.ariaLabel }, _elem.id || "toggle_".concat(_ind)));
                        }
                        else if (_elem.type === 'scaleBtn') {
                            var leftBtn_1 = _elem.elements && _elem.elements[0];
                            var rightBtn_1 = _elem.elements && _elem.elements[1];
                            return (_jsx("div", { className: "scaleWrapper", children: _jsxs("div", { className: "scaleBtnWrapper", children: [_jsx(ButtonComponent, { defaultCSS: "scaleBtn", normalCSS: "scaleBtnNormal", imgSrc: leftBtn_1 === null || leftBtn_1 === void 0 ? void 0 : leftBtn_1.imgSrc, showToolTip: true, useViewportPosition: useViewportPosition, title: leftBtn_1 === null || leftBtn_1 === void 0 ? void 0 : leftBtn_1.title, direction: displayMode === 'normal' ? 'bottom' : 'right', ariaLabel: leftBtn_1 === null || leftBtn_1 === void 0 ? void 0 : leftBtn_1.ariaLabel, isWhiteBoard: true, clickHandler: function () {
                                                customEventHandler.dispatchEvent('bottomPanel', {
                                                    id: leftBtn_1 === null || leftBtn_1 === void 0 ? void 0 : leftBtn_1.id,
                                                    type: _elem.type,
                                                    panel: 'bottomPanel',
                                                });
                                            }, disabled: isScaleDisabled() || Number(calcScale().toFixed(1)) === 0.5, ariaHidden: !isPanelOpen }, leftBtn_1 === null || leftBtn_1 === void 0 ? void 0 : leftBtn_1.id), _jsx("span", { className: "scaleVal", children: calcScale().toFixed(1) }), _jsx(ButtonComponent, { defaultCSS: "scaleBtn", normalCSS: "scaleBtnNormal", imgSrc: rightBtn_1 === null || rightBtn_1 === void 0 ? void 0 : rightBtn_1.imgSrc, showToolTip: true, useViewportPosition: useViewportPosition, direction: displayMode === 'normal' ? 'bottom' : 'right', title: rightBtn_1 === null || rightBtn_1 === void 0 ? void 0 : rightBtn_1.title, ariaLabel: rightBtn_1 === null || rightBtn_1 === void 0 ? void 0 : rightBtn_1.ariaLabel, isWhiteBoard: true, clickHandler: function () {
                                                customEventHandler.dispatchEvent('bottomPanel', {
                                                    id: rightBtn_1 === null || rightBtn_1 === void 0 ? void 0 : rightBtn_1.id,
                                                    type: _elem.type,
                                                    panel: 'bottomPanel',
                                                });
                                            }, disabled: isScaleDisabled() || Number(calcScale().toFixed(1)) === 5, ariaHidden: !isPanelOpen }, rightBtn_1 === null || rightBtn_1 === void 0 ? void 0 : rightBtn_1.id)] }) }, _elem.id || "scale_".concat(_ind)));
                        }
                        else if (_elem.type === 'lockBtn') {
                            var isLockActive = cellContentLocked ||
                                activeElementIds.includes('lock') ||
                                contentLockState().editing ||
                                contentLockState().positioning ||
                                jumpBarLengthLockState();
                            return (_jsxs("div", { className: "lockBtnWrapper", ref: lockBtnRef, children: [_jsx(ButtonComponent, { imgSrc: _elem.imgSrc, defaultCSS: "toolBtn  ".concat(isElementDisabled ||
                                            (collapseClass !== 'collapsing' && collapseClass !== 'expanded' && !open)
                                            ? 'disableButton'
                                            : ''), normalCSS: "toolBtns", 
                                        // Label / tooltip describe the NEXT action (click result),
                                        // not the current state: unlocked → "Lock", locked → "Unlock".
                                        ariaLabel: isLockActive ? t('text.unLock') : t('text.lock'), clickActive: true, title: isLockActive ? 'text.unLock' : 'text.lock', showToolTip: true, direction: displayMode === 'normal' ? 'bottom' : 'right', isParentRelative: true, isWhiteBoard: false, clickHandler: function () {
                                            var isExpandable = selectedTool.some(function (t) {
                                                return t.startsWith('tableText_') ||
                                                    t.startsWith('numberFrame_') ||
                                                    t.startsWith('text_') ||
                                                    t.startsWith('mathtext_') ||
                                                    t.startsWith('annotations_');
                                            });
                                            var hasFractionTileWithCustomLabel = selectedTool.some(function (t) { return t.startsWith('fractionTiles_'); }) &&
                                                manipulative.some(function (m) {
                                                    return m.data.type === 'fractionTiles' &&
                                                        selectedTool.some(function (t) { var _a; return t.startsWith((_a = m.data.id) !== null && _a !== void 0 ? _a : ''); }) &&
                                                        m.data.lablesType === 'custom';
                                                });
                                            var isOpenShowLock = selectedTool.some(function (t) {
                                                return t.startsWith('numberLine_') ||
                                                    t.startsWith('jumpBar_') ||
                                                    t.startsWith('coloredTiles_') ||
                                                    t.startsWith('arrayModel');
                                            }) || hasFractionTileWithCustomLabel;
                                            var isShade = selectedTool.some(function (t) { return t.startsWith('shades_'); });
                                            var isPatternShapes = selectedTool.some(function (t) {
                                                return t.startsWith('patternShapes_');
                                            });
                                            if (isExpandable || isOpenShowLock) {
                                                if (!showLock && lockBtnRef.current) {
                                                    var rect = lockBtnRef.current.getBoundingClientRect();
                                                    var spaceBelow = window.innerHeight - rect.bottom;
                                                    var spaceAbove = rect.top;
                                                    setLockAbove(spaceBelow < spaceAbove);
                                                }
                                                setShowLock(!showLock);
                                            }
                                            else if (isPatternShapes || isShade) {
                                                toggleLockState(1); // Toggle positioning lock
                                            }
                                            else {
                                                toggleLockState(); // Directly toggle lock for non-table manipulatives
                                            }
                                        }, type: 'imageType1', isActive: isLockActive, controlActiveFromOutside: true, disabled: isElementDisabled ||
                                            (collapseClass !== 'collapsing' && collapseClass !== 'expanded' && !open), ariaHidden: !isPanelOpen }), showLock &&
                                        selectedTool.some(function (t) {
                                            return t.startsWith('tableText_') ||
                                                t.startsWith('numberFrame_') ||
                                                t.startsWith('text_') ||
                                                t.startsWith('mathtext_') ||
                                                t.startsWith('annotations_');
                                        }) && (_jsx("div", { className: "lockUnlockContainer".concat(lockAbove ? ' lockAbove' : ''), children: _elem.elements &&
                                            _elem.elements.map(function (opt, index) {
                                                var isAnnotation = selectedTool.some(function (st) {
                                                    return st.startsWith('annotations_');
                                                });
                                                var isLine = isAnnotation &&
                                                    selectedTool.some(function (st) {
                                                        var _a;
                                                        var annIdx = Number(st.split('_')[1]);
                                                        return ((_a = annotations[annIdx]) === null || _a === void 0 ? void 0 : _a.type) === 'line';
                                                    });
                                                var label = isAnnotation
                                                    ? index === 0
                                                        ? isLine
                                                            ? t('text.length', { defaultValue: 'Length' })
                                                            : t('text.sizing')
                                                        : t('text.position')
                                                    : t(opt.title || '');
                                                return (_jsxs("div", { children: [_jsxs("div", { className: "container", children: [_jsx("div", { children: label }), _jsx(FlipButton, { tool: opt, defaultCSS: "toolBtn", normalCSS: "toolBtns", active: index === 0
                                                                        ? cellContentLocked ||
                                                                            activeElementIds.includes('contentLock') ||
                                                                            contentLockState().editing
                                                                        : activeElementIds.includes('positionLock') ||
                                                                            contentLockState().positioning, useActiveImageWhenDisabled: true, onClick: function () { return toggleLockState(index); }, disabled: !isAdmin ||
                                                                        (index === 1 &&
                                                                            (function () {
                                                                                var _a, _b;
                                                                                var te = selectedTool.find(function (t) {
                                                                                    return t.startsWith('tableText_');
                                                                                });
                                                                                if (!te)
                                                                                    return false;
                                                                                // Disable when cell is focused (4+ parts) or cell select mode active
                                                                                if (te.split('_').length >= 4)
                                                                                    return true;
                                                                                var tblIdx = Number(te.split('_')[1]);
                                                                                return ((_b = (_a = table[tblIdx]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.cellSelectMode) === true;
                                                                            })()) }, opt.id)] }), (_elem.elements || []).length - 1 > index && (_jsx("hr", { className: "verticleLine" }))] }, index));
                                            }) })), showLock &&
                                        selectedTool.some(function (t) {
                                            return t.startsWith('numberLine_') ||
                                                t.startsWith('jumpBar_') ||
                                                t.startsWith('coloredTiles_');
                                        }) && (_jsxs("div", { className: "lockUnlockContainer".concat(lockAbove ? ' lockAbove' : ''), children: [selectedTool.some(function (st) { return st.startsWith('numberLine_') || st.startsWith('coloredTiles_'); }) && (_jsxs(_Fragment, { children: [_jsxs("div", { children: [_jsxs("div", { className: "container", children: [_jsx("div", { children: t('text.position') }), _jsx(FlipButton, { tool: ((_d = _elem.elements) === null || _d === void 0 ? void 0 : _d[1]) || _elem, defaultCSS: "toolBtn", normalCSS: "toolBtns", active: contentLockState().positioning, onClick: function () { return toggleLockState(1); } })] }), _jsx("hr", { className: "verticleLine" })] }), _jsx("div", { children: _jsxs("div", { className: "container", children: [_jsx("div", { children: selectedTool.every(function (t) { return t.startsWith('coloredTiles'); })
                                                                        ? t('text.parts')
                                                                        : t('text.lockEditing') }), _jsx(FlipButton, { tool: ((_e = _elem.elements) === null || _e === void 0 ? void 0 : _e[0]) || _elem, defaultCSS: "toolBtn", normalCSS: "toolBtns", active: contentLockState().editing, onClick: function () { return toggleLockState(0); } })] }) })] })), selectedTool.some(function (st) { return st.startsWith('jumpBar_'); }) && (_jsxs(_Fragment, { children: [selectedTool.some(function (st) { return st.startsWith('numberLine_'); }) && (_jsx("hr", { className: "verticleLine" })), _jsx("div", { children: _jsxs("div", { className: "container", children: [_jsx("div", { children: t('text.length') }), _jsx(FlipButton, { tool: ((_f = _elem.elements) === null || _f === void 0 ? void 0 : _f[0]) || _elem, defaultCSS: "toolBtn", normalCSS: "toolBtns", active: jumpBarLengthLockState(), onClick: function () { return toggleLockState(2); } })] }) })] }))] })), showLock && selectedTool.some(function (t) { return t.startsWith('arrayModel'); }) && (_jsxs("div", { className: "lockUnlockContainer".concat(lockAbove ? ' lockAbove' : ''), children: [_jsxs("div", { children: [_jsxs("div", { className: "container", children: [_jsx("div", { children: t('text.position') }), _jsx(FlipButton, { tool: ((_g = _elem.elements) === null || _g === void 0 ? void 0 : _g[1]) || _elem, defaultCSS: "toolBtn", normalCSS: "toolBtns", active: contentLockState().positioning, onClick: function () { return toggleLockState(1); } })] }), _jsx("hr", { className: "verticleLine" })] }), _jsx("div", { children: _jsxs("div", { className: "container", children: [_jsx("div", { children: t('text.parts') }), _jsx(FlipButton, { tool: ((_h = _elem.elements) === null || _h === void 0 ? void 0 : _h[0]) || _elem, defaultCSS: "toolBtn", normalCSS: "toolBtns", active: contentLockState().editing, onClick: function () { return toggleLockState(0); } })] }) })] })), showLock &&
                                        selectedTool.some(function (t) { return t.startsWith('fractionTiles_'); }) &&
                                        (function () {
                                            var _a, _b;
                                            var hasCustomLabel = manipulative.some(function (m) {
                                                return m.data.type === 'fractionTiles' &&
                                                    selectedTool.some(function (t) { var _a; return t.startsWith((_a = m.data.id) !== null && _a !== void 0 ? _a : ''); }) &&
                                                    m.data.lablesType === 'custom';
                                            });
                                            return (_jsxs("div", { className: "lockUnlockContainer".concat(lockAbove ? ' lockAbove' : ''), children: [_jsxs("div", { children: [_jsxs("div", { className: "container", children: [_jsx("div", { children: t('text.position') }), _jsx(FlipButton, { tool: ((_a = _elem.elements) === null || _a === void 0 ? void 0 : _a[1]) || _elem, defaultCSS: "toolBtn", normalCSS: "toolBtns", active: contentLockState().positioning, onClick: function () { return toggleLockState(1); } })] }), hasCustomLabel && _jsx("hr", { className: "verticleLine" })] }), hasCustomLabel && (_jsx("div", { children: _jsxs("div", { className: "container", children: [_jsx("div", { children: t('text.label', { defaultValue: 'Label' }) }), _jsx(FlipButton, { tool: ((_b = _elem.elements) === null || _b === void 0 ? void 0 : _b[0]) || _elem, defaultCSS: "toolBtn", normalCSS: "toolBtns", active: contentLockState().editing, onClick: function () { return toggleLockState(0); } })] }) }))] }));
                                        })()] }, _elem.id));
                        }
                        else if (_elem.type === 'flipBtn') {
                            // Check activeElementIds first; for the group button also check
                            // if a pattern-shapes group is currently selected (mirrors how the
                            // lock button reads state directly via contentLockState()).
                            var isActive_1 = activeElementIds.includes((_j = _elem.id) !== null && _j !== void 0 ? _j : '');
                            if (_elem.id === 'group' &&
                                !isActive_1 &&
                                selectedTool.length === 1 &&
                                selectedTool[0].startsWith('manipulativeGroup_')) {
                                var members = manipulative.filter(function (m) { return m.data.groupId === selectedTool[0]; });
                                var allPatternShapes = members.length > 0 && members.every(function (m) { return m.data.type === 'patternShapes'; });
                                var allCtOrLp = members.length > 0 &&
                                    members.every(function (m) { return m.data.type === 'coloredTiles' || m.data.type === 'linearPiece'; }) &&
                                    members.some(function (m) { return m.data.type === 'coloredTiles'; });
                                if (allPatternShapes || allCtOrLp) {
                                    isActive_1 = true;
                                }
                            }
                            return (_jsx(FlipButton, { tool: _elem, defaultCSS: "collapsibleOptionBtn ".concat(_elem.id, " ").concat(elemsDefaultCSS), normalCSS: elemsNormalCSS, active: isActive_1, onClick: function () {
                                    var _a;
                                    customEventHandler.dispatchEvent('bottomPanel', {
                                        id: isActive_1 ? (_a = _elem.activeId) !== null && _a !== void 0 ? _a : _elem.id : _elem.id,
                                        type: _elem.type,
                                        panel: 'bottomPanel',
                                    });
                                }, disabled: isElementDisabled ||
                                    (collapseClass !== 'collapsing' && collapseClass !== 'expanded' && !open), showToolTip: (_elem === null || _elem === void 0 ? void 0 : _elem.title) !== undefined, isParentRelative: isInnerTooltipRelative !== null && isInnerTooltipRelative !== void 0 ? isInnerTooltipRelative : isTooltipRelative, useViewportPosition: useViewportPosition, direction: ((parentType === 'footerBtn' ? _elem === null || _elem === void 0 ? void 0 : _elem.tDirectionFooter : _elem === null || _elem === void 0 ? void 0 : _elem.tDirection) ||
                                    direction), ariaHidden: !isPanelOpen }, "flipBtn_".concat(_ind)));
                        }
                        else {
                            return (_jsx(ButtonComponent, { imgSrc: _elem.imgSrc, defaultCSS: "collapsibleOptionBtn ".concat(elemsDefaultCSS), normalCSS: elemsNormalCSS, ariaLabel: _elem.ariaLabel, text: _elem.text ? t(_elem.text) : '', clickHandler: function (e) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    selectItem(originalIndex);
                                }, onTouchEnd: function (e) {
                                    var _a;
                                    // iPad Safari fallback: on iPad, tapping an inner stamp
                                    // shape fires touchstart/touchend/mousedown but the
                                    // synthesized `click` is silently dropped by Safari
                                    // (likely because the state change that opened the
                                    // drawer interferes with click timing). Catching the
                                    // touchend directly guarantees selection. Touchmove
                                    // would suggest a drag/scroll — in that case we skip.
                                    var touch = (_a = e.changedTouches) === null || _a === void 0 ? void 0 : _a[0];
                                    if (!touch)
                                        return;
                                    // If the button is disabled, don't hijack touchend.
                                    var btn = e.currentTarget;
                                    if (btn.disabled)
                                        return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    selectItem(originalIndex);
                                }, isActive: activeElements !== null ? activeElements === originalIndex : undefined, clickActive: activeElements !== null ? activeElements === originalIndex : undefined, disabled: isElementDisabled ||
                                    (collapseClass !== 'collapsing' && collapseClass !== 'expanded' && !open), reference: function (el) {
                                    optionRefs.current[_ind] = el;
                                }, showToolTip: (_elem === null || _elem === void 0 ? void 0 : _elem.title) !== undefined, title: _elem === null || _elem === void 0 ? void 0 : _elem.title, isWhiteBoard: isWhiteBoard, isParentRelative: isInnerTooltipRelative !== null && isInnerTooltipRelative !== void 0 ? isInnerTooltipRelative : isTooltipRelative, useViewportPosition: useViewportPosition, type: innerBtnType || type, direction: (parentType === 'footerBtn' ? _elem === null || _elem === void 0 ? void 0 : _elem.tDirectionFooter : _elem === null || _elem === void 0 ? void 0 : _elem.tDirection) ||
                                    direction, ariaHidden: !isPanelOpen }, "btn_".concat(_ind)));
                        }
                    }) })), hr && _jsx("hr", { className: "verticalHr", "aria-hidden": "true" })] }));
};
export default CollapsibleDiv;
